/**
 * useThrottledMarketVersion — the positions page's view of the market-store
 * tick, capped at POSITIONS_TICK_MS (250ms = 4 Hz).
 *
 * The store's own `version` bumps at up to ~30 Hz under game load
 * (marketStore's RAF-coalesced flush). Subscribing the 3,500-line
 * PositionTracker body to that raw counter makes every bump re-run the whole
 * page — sorts, VM builds, row renders — which is the diagnosed main-thread
 * saturator behind the hydra 8 MiB disconnect loop. 4 Hz matches the
 * freshness floor of the data this page shows anyway (ORG_POLL_MS floors at
 * 250ms; push MIN_INTERVAL floors orders/pos frames at 250ms), so rendering
 * faster than this only repaints unchanged rows.
 *
 * ONE module-level throttled source is shared by every subscriber (the page
 * body, usePositionPnl in each of its mounts, popouts): one store
 * subscription, one trailing timer, and every listener notified in the same
 * task so React batches them into a single commit — N hook instances never
 * multiply the render cadence. Ref-counted; tears down with the last
 * subscriber. performance.now() (monotonic) so a wall-clock step can't wedge
 * the throttle.
 *
 * Leading edge + trailing timer: a bump after a quiet spell renders
 * immediately; a burst coalesces so the LAST tick always lands. Other views
 * (ValueDashboard, StreamRift) keep their full-rate subscriptions — this is
 * a per-consumer choice, NOT a store-level cap.
 *
 * Dialing: cadence is this one constant. 250 → 4 Hz (current, team pick),
 * 1000 → 1 Hz fallback if a heavy portfolio still measures hot.
 */
import { useSyncExternalStore } from 'react';
import { useMarketStore } from '../../stores/marketStore';

export const POSITIONS_TICK_MS = 250;

interface SharedSource {
  throttled: number;
  latest: number;
  lastEmit: number;
  timer: number | null;
  listeners: Set<() => void>;
  unsubStore: () => void;
}

let source: SharedSource | null = null;

function emit(s: SharedSource): void {
  s.lastEmit = performance.now();
  s.timer = null;
  s.throttled = s.latest;
  for (const notify of s.listeners) notify();
}

function ensureSource(): SharedSource {
  if (source) return source;
  const initial = useMarketStore.getState().version;
  const s: SharedSource = {
    throttled: initial,
    latest: initial,
    lastEmit: 0,
    timer: null,
    listeners: new Set(),
    unsubStore: () => {},
  };
  s.unsubStore = useMarketStore.subscribe((st) => {
    if (st.version === s.latest) return;
    s.latest = st.version;
    const elapsed = performance.now() - s.lastEmit;
    if (elapsed >= POSITIONS_TICK_MS) {
      emit(s); // leading edge — first tick after a quiet spell lands instantly
    } else if (s.timer == null) {
      // trailing — the last tick of a burst always lands
      s.timer = window.setTimeout(() => emit(s), POSITIONS_TICK_MS - elapsed);
    }
  });
  source = s;
  return s;
}

function teardown(s: SharedSource): void {
  s.unsubStore();
  if (s.timer != null) window.clearTimeout(s.timer);
  if (source === s) source = null;
}

function subscribe(onChange: () => void): () => void {
  const s = ensureSource();
  s.listeners.add(onChange);
  return () => {
    s.listeners.delete(onChange);
    if (s.listeners.size === 0) teardown(s);
  };
}

function getSnapshot(): number {
  return ensureSource().throttled;
}

/** Test hook: drop the shared source so suites don't leak throttle state. */
export function _resetThrottledMarketVersionForTests(): void {
  if (source) teardown(source);
}

export function useThrottledMarketVersion(): number {
  return useSyncExternalStore(subscribe, getSnapshot);
}
